import {
  TUser,
  TUserContactInfo,
  TUserDashboard,
  TUserDemographicsInfoWithoutPicture,
  TUserLocation,
} from '@/types/User.types';
import { demographicsFeToBe, userBeToFe } from '@/utils/demographicDataMappers';
import { api, TCustomAxiosResponse } from '..';
import { apiPaths } from '@/services/api/paths';
import { WELLNESS_DATA_MULTIPLIER } from '@/constants';
import { THealthDomainScores } from '@/types/Health.types';
import { TNotificationChannel } from '@/types/Notification.types';
import type { VerifyTokenResponse, GenericApiResponseDemographicsSkipResponseDto } from '@/types/generated/schemas';

export const userGet = async (): Promise<TUser> => {
  const { data } = await api.get(apiPaths.user.root);
  const user = userBeToFe(data.data) as TUser;

  return user;
};

export const skipDemographicsPost = async (): Promise<string> => {
  const { data } = await api.post<GenericApiResponseDemographicsSkipResponseDto>(apiPaths.user.skipDemographics);
  return data.data.demographicsSkippedAt;
};

export const userDashboardGet = async (): Promise<TUserDashboard> => {
  const { data } = await api.get<{ data: TUserDashboard }>(apiPaths.user.dashboard);

  const wellnessDomainsMultiplied = data.data.score.domains;

  for (const key in wellnessDomainsMultiplied) {
    if (typeof wellnessDomainsMultiplied[key as keyof THealthDomainScores] == 'number' && key !== 'totalScore') {
      wellnessDomainsMultiplied[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
    }
  }

  return {
    ...data.data,
    score: {
      ...data.data.score,
      domains: wellnessDomainsMultiplied,
    },
  };
};

export const userNotificationPreferencesGet = async (): Promise<TNotificationChannel[]> => {
  const { data } = await api.get<{ channels: TNotificationChannel[] }>(apiPaths.user.notificationPreferences);

  return data.channels;
};

export const updateUserNotificationPreferencesPatch = async (
  newNotificationPreferences: TNotificationChannel[],
): Promise<string> => {
  const { data } = await api.patch<TCustomAxiosResponse<string>>(apiPaths.user.notificationPreferences, {
    channels: newNotificationPreferences,
  });

  return data.data;
};

export const signInPost = async (email: string, password: string): Promise<TUser> => {
  const res = await api.post(apiPaths.user.signIn, {
    email,
    password,
  });

  return userBeToFe(res.data.data);
};

export const signOutPost = async (): Promise<TUser> => {
  const res = await api.post(apiPaths.user.signOut);

  return res.data.data;
};

export const forgotPasswordPost = async (email: string) => {
  await api.post(apiPaths.user.forgotPassword, {
    email,
  });

  return 'ok';
};

export const updateUserNSubmitEulaPut = async (info: TUserContactInfo): Promise<TUser> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { email, ...restForm } = info;

  const { data } = await api.put<TCustomAxiosResponse<TUser>>(apiPaths.user.root, {
    ...restForm,
  });

  const user = data.data;

  return user;
};

export const updateUserDemoGraphicsPut = async (info: TUserDemographicsInfoWithoutPicture, onboarding = false): Promise<TUser> => {
  const userDemographicsBeMapped = demographicsFeToBe(info);
  if (onboarding) {
    delete userDemographicsBeMapped.occupation;
    delete userDemographicsBeMapped.occupationOther;
  }

  const { data } = await api.put<TCustomAxiosResponse<TUser>>(apiPaths.user.root, {
    ...userDemographicsBeMapped,
  });

  const user = userBeToFe(data.data);
  return user;
};

export const updateUserLocationPatch = async (location: TUserLocation): Promise<TUser> => {
  const { data } = await api.patch<TCustomAxiosResponse<TUser>>(apiPaths.user.location, {
    ...location,
  });

  const user = userBeToFe(data.data);
  return user;
};

export const uploadProfilePhoto = async (photo: File): Promise<TUser> => {
  const formData = new FormData();
  formData.append('file', new File([photo], photo.name));

  const { data } = await api.post(apiPaths.user.uploadProfilePicture, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return userBeToFe(data.data);
};

export const updateUserContactDetails = async (userContactDetails: Omit<TUserContactInfo, 'email'>): Promise<TUser> => {
  const { data } = await api.put(apiPaths.user.root, userContactDetails);

  const updatedUser: TUser = userBeToFe(data.data);
  return updatedUser;
};

export const verifyAuthToken = async (body: { token: string; email: string }): Promise<VerifyTokenResponse['data']> => {
  const { data } = await api.post<VerifyTokenResponse>(apiPaths.user.verifyAuthToken, body);
  return data.data;
};

export const verifyEmailToken = async (body: { token: string; email: string }): Promise<{ success: boolean }> => {
  const { data } = await api.post(apiPaths.user.verifyEmail, body);
  return data.data;
};

export const verifyResetAuthToken = async (body: { token: string; email: string }): Promise<{ success: boolean }> => {
  const { data } = await api.post(apiPaths.user.verifyResetAuthToken, body);
  return data.data;
};

export const setInitialUserPassword = async (body: {
  email: string;
  token: string;
  newPassword: string;
}): Promise<{ success: boolean }> => {
  const { data } = await api.post(apiPaths.user.setInitialPassword, body);
  return data.data;
};

export const resetUserPassword = async (body: {
  email: string;
  token: string;
  newPassword: string;
}): Promise<{ success: boolean }> => {
  const { data } = await api.post(apiPaths.user.resetPassword, body);
  return data.data;
};

export const deleteOwnAccount = async (): Promise<{ success: boolean }> => {
  const { data } = await api.delete(apiPaths.user.deleteOwnAccount);
  return data.data;
};

export const resendVerificationEmail = async (): Promise<{ success: boolean; message?: string }> => {
  const { data } = await api.post<TCustomAxiosResponse<{ success: boolean; message?: string }>>(
    apiPaths.user.resendVerificationEmail,
  );
  return data.data;
};
